import React, { useState, useEffect } from 'react';
import UiContent from "../../Components/Common/UiContent";
import BreadCrumb from '../../Components/Common/BreadCrumb';
import ResearchDisclaimer from '../../Components/Common/ResearchDisclaimer';
import { getFirebaseBackend } from "../../helpers/firebase_helper";
import config from '../../config';
import { 
    Card, 
    CardBody, 
    Col, 
    Container, 
    Row, 
    Table,
    Badge,
    Button,
    Input,
    InputGroup,
    InputGroupText,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Alert,
    Spinner,
    FormGroup,
    Label,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter
} from 'reactstrap';
import PreviewCardHeader from '../../Components/Common/PreviewCardHeader';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Flatpickr from "react-flatpickr";
import Pagination from '../../Components/Common/Pagination';
import '../styles/predictions.css';

const PredictionsList = () => {
    document.title = "Predictions List | AI Financial Prediction Tracker";

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const isCleanMode = searchParams.get('clean') === 'true';
    const [predictions, setPredictions] = useState([]);
    const [filteredPredictions, setFilteredPredictions] = useState([]);
    const [filters, setFilters] = useState({
        symbol: searchParams.get('symbol') || '',
        model: searchParams.get('model') || '',
        status: searchParams.get('status') || '',
        prediction_type: searchParams.get('prediction_type') || '',
        user_id: searchParams.get('user_id') || '',
        created_by: searchParams.get('created_by') || ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showMyPredictionsOnly, setShowMyPredictionsOnly] = useState(searchParams.get('my') === 'true');
    const [currentUser, setCurrentUser] = useState(null);
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
    const [totalPredictions, setTotalPredictions] = useState(0);
    const [paginatedPredictions, setPaginatedPredictions] = useState([]);
    const perPageData = 100;
    
    // Calendar state
    const [selectedDate, setSelectedDate] = useState(null);
    const [showDateModal, setShowDateModal] = useState(false);
    const [modalSelectedDate, setModalSelectedDate] = useState(null);

    // Function to handle prediction row clicks
    const handlePredictionClick = (predictionId, event) => {
        // Check if the clicked element is a link or inside a link
        const target = event.target;
        const isLinkClick = target.closest('a') || target.closest('button') || target.closest('.dropdown');
        
        if (isLinkClick) {
            return; // Don't navigate if clicking on a link or button
        }
        
        const baseUrl = `/prediction/${predictionId}`;
        const url = isCleanMode ? `${baseUrl}?clean=true` : baseUrl;
        
        // Open in new tab with proper window features
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        
        // Fallback if popup was blocked
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            // Navigate in same window as fallback
            window.location.href = url;
        }
    };

    // Get current user info on mount
    useEffect(() => {
        const firebaseBackend = getFirebaseBackend();
        if (firebaseBackend) {
            firebaseBackend.getCurrentUser()
                .then(user => {
                    if (user) {
                        setCurrentUser(user);
                    }
                })
                .catch(err => {
                    console.error("Error getting current user:", err);
                });
        }
    }, []);

    useEffect(() => {
        // Load predictions from API
        const fetchPredictions = async () => {
            setLoading(true);
            setError(null);
            
            try {
                // Build query parameters
                const params = new URLSearchParams();
                if (filters.symbol) params.append('symbol', filters.symbol);
                if (filters.model) params.append('model', filters.model);
                if (filters.prediction_type) params.append('prediction_type', filters.prediction_type);
                if (filters.user_id) params.append('user_id', filters.user_id);
                if (filters.created_by) params.append('created_by', filters.created_by);
                if (showMyPredictionsOnly && currentUser) {
                    params.append('created_by', currentUser.uid);
                }
                
                // Pagination parameters
                const offset = (currentPage - 1) * perPageData;
                params.append('limit', perPageData.toString());
                params.append('offset', offset.toString());
                
                const response = await fetch(`${config.api.API_URL}/api/predictions?${params}`, {
                    credentials: 'include'
                });
                
                if (response.ok) {
                    const data = await response.json();
                    const predictionsArray = data.predictions || [];
                    setPredictions(predictionsArray);
                    setFilteredPredictions(predictionsArray);
                    setTotalPredictions(data.total || predictionsArray.length);
                } else {
                    throw new Error('Failed to fetch predictions');
                }
            } catch (error) {
                console.error('Error fetching predictions:', error);
                setError('Failed to load predictions. Please try again.');
                setPredictions([]);
                setFilteredPredictions([]);
                setTotalPredictions(0);
            } finally {
                setLoading(false);
            }
        };

        fetchPredictions();
    }, [filters, showMyPredictionsOnly, currentUser, currentPage]); // Re-fetch when filters, my predictions toggle, current user, or page change

    useEffect(() => {
        // Apply client-side search filtering
        let filtered = predictions;

        if (searchTerm) {
            filtered = filtered.filter(pred => 
                pred.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
                pred.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (pred.reason && pred.reason.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (pred.created_by && pred.created_by.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (pred.user_name && pred.user_name.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        setFilteredPredictions(filtered);
    }, [predictions, searchTerm]);

    useEffect(() => {
        // Filter predictions based on search term (client-side filtering for search)
        let filtered = predictions;

        if (searchTerm) {
            filtered = filtered.filter(pred => 
                pred.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
                pred.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (pred.reason && pred.reason.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (pred.created_by && pred.created_by.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (pred.user_name && pred.user_name.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        setFilteredPredictions(filtered);
    }, [predictions, searchTerm]);

    // Handle filter changes and update URL
    const handleFilterChange = (filterName, value) => {
        const newFilters = { ...filters, [filterName]: value };
        setFilters(newFilters);
        setCurrentPage(1); // Reset to first page when filters change
        
        // Update URL parameters
        const newParams = new URLSearchParams();
        Object.entries(newFilters).forEach(([key, val]) => {
            if (val) newParams.append(key, val);
        });
        if (showMyPredictionsOnly) newParams.set('my', 'true');
        newParams.set('page', '1');
        setSearchParams(newParams);
    };

    // Handle "My Predictions" toggle
    const handleMyPredictionsToggle = (checked) => {
        setShowMyPredictionsOnly(checked);
        setCurrentPage(1); // Reset to first page
        
        // Update URL parameters
        const newParams = new URLSearchParams(searchParams);
        if (checked) {
            newParams.set('my', 'true');
        } else {
            newParams.delete('my');
        }
        newParams.set('page', '1');
        setSearchParams(newParams);
    };

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
        
        // Update URL parameters
        const newParams = new URLSearchParams(searchParams);
        newParams.set('page', page.toString());
        setSearchParams(newParams);
    };

    // Handle calendar date selection
    const handleDateSelect = (selectedDates) => {
        if (selectedDates && selectedDates.length > 0) {
            const date = selectedDates[0];
            setSelectedDate(date);
            setModalSelectedDate(date);
            setShowDateModal(true);
        }
    };

    // Handle date type selection from modal
    const handleDateTypeSelection = (type) => {
        if (modalSelectedDate) {
            const year = modalSelectedDate.getFullYear();
            const month = modalSelectedDate.getMonth() + 1;
            const day = modalSelectedDate.getDate();
            
            navigate(`/predictions-by-date/${year}/${month}/${day}?type=${type}`);
        }
        setShowDateModal(false);
    };

    // Clear all filters
    const clearFilters = () => {
        setFilters({
            symbol: '',
            model: '',
            status: '',
            prediction_type: '',
            user_id: '',
            created_by: ''
        });
        setSearchTerm('');
        setShowMyPredictionsOnly(false);
        setCurrentPage(1);
        setSearchParams({ page: '1' });
    };

    // Get unique values for filter dropdowns
    const getUniqueValues = (field) => {
        return [...new Set(predictions.map(pred => pred[field]).filter(Boolean))];
    };

    // Format date - now using proper UTC timezone conversion
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        
        let date;
        
        // Handle MySQL DATETIME format (YYYY-MM-DD HH:MM:SS)
        // These values are stored in UTC in the database
        if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
            // Add 'Z' to indicate this is UTC time, so JavaScript converts it properly to local
            const convertedString = dateString.replace(' ', 'T') + 'Z';
            date = new Date(convertedString);
        } else {
            // Handle ISO strings and other formats
            date = new Date(dateString);
        }
        
        // Check if date is valid
        if (isNaN(date.getTime())) return 'Invalid Date';
        
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get status badge
    const getStatusBadge = (prediction) => {
        const now = new Date();
        const validUntil = new Date(prediction.valid_until);
        
        if (prediction.actual_price !== null && prediction.actual_price !== undefined) {
            // Prediction has been scored
            const currentPrice = prediction.current_price;
            const actualPrice = prediction.actual_price;
            const rangeMin = prediction.range_min;
            const rangeMax = prediction.range_max;
            
            if (actualPrice >= rangeMin && actualPrice <= rangeMax) {
                return <Badge color="success">Success</Badge>;
            } else {
                return <Badge color="danger">Failed</Badge>;
            }
        } else if (validUntil <= now) {
            return <Badge color="warning">Expired</Badge>;
        } else {
            return <Badge color="info">Active</Badge>;
        }
    };

    // Get performance indicator
    const getPerformanceIndicator = (prediction) => {
        if (prediction.actual_price === null || prediction.actual_price === undefined) {
            return '-';
        }

        const currentPrice = prediction.current_price;
        const actualPrice = prediction.actual_price;
        const change = ((actualPrice - currentPrice) / currentPrice) * 100;
        
        return (
            <span className={change >= 0 ? 'text-success' : 'text-danger'}>
                {change >= 0 ? '+' : ''}{change.toFixed(2)}%
            </span>
        );
    };

    return (
        <React.Fragment>
            <UiContent />
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Predictions List" pageTitle="Predictions" />
                    <ResearchDisclaimer className="mb-3" />
                    
                    {error && (
                        <Alert color="danger">
                            {error}
                        </Alert>
                    )}

                    <Row>
                        <Col xl={12}>
                            <Card>
                                <PreviewCardHeader title="All Predictions" />
                                <CardBody>
                                    <div className="d-flex justify-content-end mb-3">
                                        <small className="text-muted">
                                            <i className="ri-time-zone-line me-1"></i>
                                            Times shown in your local timezone
                                        </small>
                                    </div>
                                    
                                    {/* Calendar Section */}
                                    <Row className="mb-4">
                                        <Col lg={3}>
                                            <Card className="border">
                                                <CardBody className="p-3">
                                                    <h6 className="mb-3">
                                                        <i className="ri-calendar-line me-2"></i>
                                                        View by Date
                                                    </h6>
                                                    <div className="calendar-container">
                                                        <Flatpickr
                                                            className="form-control"
                                                            placeholder="Select a date..."
                                                            options={{
                                                                inline: true,
                                                                dateFormat: "Y-m-d",
                                                                maxDate: "today"
                                                            }}
                                                            onChange={handleDateSelect}
                                                            value={selectedDate}
                                                        />
                                                    </div>
                                                    <small className="text-muted mt-2 d-block">
                                                        Click a date to view predictions created or expired on that day
                                                    </small>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col lg={9}>
                                            {/* Filters */}
                                            <Row className="mb-4">
                                                <Col md={3}>
                                                    <div className="search-box">
                                                        <Input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Search predictions..."
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                        />
                                                        <i className="ri-search-line search-icon"></i>
                                                    </div>
                                                </Col>
                                                <Col md={2}>
                                                    <select 
                                                        className="form-select" 
                                                        value={filters.symbol}
                                                        onChange={(e) => handleFilterChange('symbol', e.target.value)}
                                                    >
                                                        <option value="">All Symbols</option>
                                                        {getUniqueValues('symbol').map(symbol => (
                                                            <option key={symbol} value={symbol}>{symbol}</option>
                                                        ))}
                                                    </select>
                                                </Col>
                                                <Col md={2}>
                                                    <select 
                                                        className="form-select" 
                                                        value={filters.prediction_type}
                                                        onChange={(e) => handleFilterChange('prediction_type', e.target.value)}
                                                    >
                                                        <option value="">All Types</option>
                                                        <option value="AI">AI</option>
                                                        <option value="MANUAL">Manual</option>
                                                        <option value="HUMAN">Human</option>
                                                        <option value="YOUTUBE">YouTube</option>
                                                        <option value="TWITTER">Twitter</option>
                                                    </select>
                                                </Col>
                                                <Col md={2}>
                                                    <select 
                                                        className="form-select" 
                                                        value={filters.status}
                                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                                    >
                                                        <option value="">All Status</option>
                                                        <option value="active">Active</option>
                                                        <option value="expired">Expired</option>
                                                        <option value="success">Success</option>
                                                        <option value="failed">Failed</option>
                                                    </select>
                                                </Col>
                                                <Col md={3}>
                                                    <div className="d-flex gap-2 align-items-center">
                                                        <Button color="soft-secondary" onClick={clearFilters}>
                                                            <i className="ri-refresh-line me-1"></i>
                                                            Clear Filters
                                                        </Button>
                                                        {(filters.user_id || filters.created_by) && (
                                                            <Badge color="info" className="px-3 py-2">
                                                                Filtered by User: {filters.created_by || `User ${filters.user_id}`}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                    
                                    {/* My Predictions Toggle */}
                                    {currentUser && (
                                        <Row className="mb-3">
                                            <Col>
                                                <FormGroup check className="mb-0">
                                                    <Input
                                                        type="checkbox"
                                                        id="myPredictionsCheck"
                                                        checked={showMyPredictionsOnly}
                                                        onChange={(e) => handleMyPredictionsToggle(e.target.checked)}
                                                    />
                                                    <Label check htmlFor="myPredictionsCheck" className="fw-medium">
                                                        <i className="ri-user-line me-1"></i>
                                                        Show only my predictions
                                                    </Label>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    )}

                                    {/* Loading State */}
                                    {loading && (
                                        <div className="text-center py-5">
                                            <Spinner color="primary" />
                                            <p className="mt-2">Loading predictions...</p>
                                        </div>
                                    )}

                                    {/* Predictions Table */}
                                    {!loading && (
                                        <div className="table-responsive">
                                            <Table className="table-nowrap align-middle mb-0">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">Asset</th>
                                                        <th scope="col">Model/Predictor</th>
                                                        <th scope="col">Created By</th>
                                                        <th scope="col">Price Range</th>
                                                        <th scope="col">Current → Actual</th>
                                                        <th scope="col">Performance</th>
                                                        <th scope="col">Confidence</th>
                                                        <th scope="col">Type</th>
                                                        <th scope="col">Privacy</th>
                                                        <th scope="col">Status</th>
                                                        <th scope="col">Created</th>
                                                        <th scope="col">Valid Until</th>
                                                        <th scope="col">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredPredictions.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="13" className="text-center py-4">
                                                                <div className="text-muted">
                                                                    <i className="ri-search-line fs-1 mb-2 d-block"></i>
                                                                    No predictions found matching your criteria.
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        filteredPredictions.map((prediction) => (
                                                            <tr 
                                                                key={prediction.id}
                                                                onClick={(e) => handlePredictionClick(prediction.id, e)}
                                                                style={{ cursor: 'pointer' }}
                                                                className="table-row-hover"
                                                            >
                                                                <td>
                                                                    <div className="d-flex align-items-center">
                                                                        <div className="flex-shrink-0">
                                                                            <div className="avatar-xs">
                                                                                <div className="avatar-title rounded-circle bg-light text-body">
                                                                                    {prediction.symbol.charAt(0)}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="flex-grow-1 ms-2">
                                                                            <h6 className="mb-0">{prediction.symbol}</h6>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <span className="fw-medium">{prediction.model}</span>
                                                                </td>
                                                                <td>
                                                                    {prediction.user_id ? (
                                                                        <Link 
                                                                            to={`/predictions/list?user_id=${prediction.user_id}`}
                                                                            className="text-decoration-none"
                                                                        >
                                                                            <div className="d-flex align-items-center">
                                                                                {prediction.user_avatar && (
                                                                                    <img 
                                                                                        src={prediction.user_avatar} 
                                                                                        alt={prediction.user_name || prediction.created_by}
                                                                                        className="rounded-circle me-2"
                                                                                        style={{ width: '24px', height: '24px' }}
                                                                                    />
                                                                                )}
                                                                                <span className="text-primary">
                                                                                    {prediction.user_name || prediction.created_by || 'Unknown User'}
                                                                                </span>
                                                                            </div>
                                                                        </Link>
                                                                    ) : (
                                                                        <span className="text-muted">
                                                                            {prediction.created_by || 'System'}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <div className="text-nowrap">
                                                                        <small className="text-muted">Range:</small><br/>
                                                                        ${prediction.range_min?.toLocaleString()} - ${prediction.range_max?.toLocaleString()}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div className="text-nowrap">
                                                                        <div>${prediction.current_price?.toLocaleString()}</div>
                                                                        <div className="text-muted">
                                                                            → {prediction.actual_price ? `$${prediction.actual_price.toLocaleString()}` : 'Pending'}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    {getPerformanceIndicator(prediction)}
                                                                </td>
                                                                <td>
                                                                    {prediction.confidence ? (
                                                                        <Badge color="secondary" pill>
                                                                            {prediction.confidence}%
                                                                        </Badge>
                                                                    ) : (
                                                                        <span className="text-muted">-</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <Badge 
                                                                        color={
                                                                            prediction.prediction_type === 'AI_WITH_TA' ? 'info' :
                                                                            prediction.prediction_type === 'AI_WITHOUT_TA' ? 'info' :
                                                                            prediction.prediction_type === 'MANUAL' ? 'primary' :
                                                                            prediction.prediction_type === 'HUMAN' ? 'success' :
                                                                            'secondary'
                                                                        }
                                                                        pill
                                                                    >
                                                                        {prediction.prediction_type}
                                                                    </Badge>
                                                                </td>
                                                                <td>
                                                                    {prediction.is_private ? (
                                                                        <Badge color="warning" pill>
                                                                            <i className="ri-lock-line me-1"></i>
                                                                            Private
                                                                        </Badge>
                                                                    ) : (
                                                                        <Badge color="success" pill>
                                                                            <i className="ri-global-line me-1"></i>
                                                                            Public
                                                                        </Badge>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {getStatusBadge(prediction)}
                                                                </td>
                                                                <td>
                                                                    <div className="text-nowrap text-muted">
                                                                        {formatDate(prediction.input_time)}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div className="text-nowrap text-muted">
                                                                        {formatDate(prediction.valid_until)}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <UncontrolledDropdown>
                                                                        <DropdownToggle
                                                                            href="#"
                                                                            className="btn btn-soft-secondary btn-sm"
                                                                            tag="button"
                                                                        >
                                                                            <i className="ri-more-fill"></i>
                                                                        </DropdownToggle>
                                                                        <DropdownMenu>
                                                                            <DropdownItem
                                                                                onClick={() => {
                                                                                    const baseUrl = `/prediction/${prediction.id}`;
                                                                                    const url = isCleanMode ? `${baseUrl}?clean=true` : baseUrl;
                                                                                    window.open(url, '_blank', 'noopener,noreferrer');
                                                                                }}
                                                                            >
                                                                                <i className="ri-eye-fill align-bottom me-2 text-muted"></i>
                                                                                View Details
                                                                            </DropdownItem>
                                                                            {prediction.user_id && (
                                                                                <DropdownItem>
                                                                                    <Link 
                                                                                        to={`/predictions/list?user_id=${prediction.user_id}`}
                                                                                        className="dropdown-item"
                                                                                    >
                                                                                        <i className="ri-user-line align-bottom me-2 text-muted"></i>
                                                                                        View User's Predictions
                                                                                    </Link>
                                                                                </DropdownItem>
                                                                            )}
                                                                            <DropdownItem divider />
                                                                            <DropdownItem>
                                                                                <i className="ri-share-line align-bottom me-2 text-muted"></i>
                                                                                Share
                                                                            </DropdownItem>
                                                                        </DropdownMenu>
                                                                    </UncontrolledDropdown>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    )}

                                    {/* Results Summary and Pagination */}
                                    {!loading && filteredPredictions.length > 0 && (
                                        <Row className="mt-3">
                                            <Col sm={6}>
                                                <div className="text-muted">
                                                    Showing {((currentPage - 1) * perPageData) + 1} to {Math.min(currentPage * perPageData, totalPredictions)} of {totalPredictions} prediction{totalPredictions !== 1 ? 's' : ''}
                                                    {searchTerm && ` matching "${searchTerm}"`}
                                                </div>
                                            </Col>
                                            <Col sm={6}>
                                                {totalPredictions > perPageData && (
                                                    <div className="d-flex justify-content-end">
                                                        <nav aria-label="Page navigation">
                                                            <ul className="pagination pagination-sm mb-0">
                                                                <li className={`page-item ${currentPage <= 1 ? 'disabled' : ''}`}>
                                                                    <Button
                                                                        color="light"
                                                                        size="sm"
                                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                                        disabled={currentPage <= 1}
                                                                    >
                                                                        <i className="ri-arrow-left-line"></i>
                                                                        Previous
                                                                    </Button>
                                                                </li>
                                                                
                                                                {/* Page Numbers */}
                                                                {(() => {
                                                                    const totalPages = Math.ceil(totalPredictions / perPageData);
                                                                    const maxVisiblePages = 5;
                                                                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                                                                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                                                                    
                                                                    if (endPage - startPage < maxVisiblePages - 1) {
                                                                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                                                                    }
                                                                    
                                                                    const pages = [];
                                                                    for (let i = startPage; i <= endPage; i++) {
                                                                        pages.push(
                                                                            <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                                                                <Button
                                                                                    color={currentPage === i ? "primary" : "light"}
                                                                                    size="sm"
                                                                                    onClick={() => handlePageChange(i)}
                                                                                    className="mx-1"
                                                                                >
                                                                                    {i}
                                                                                </Button>
                                                                            </li>
                                                                        );
                                                                    }
                                                                    return pages;
                                                                })()}
                                                                
                                                                <li className={`page-item ${currentPage >= Math.ceil(totalPredictions / perPageData) ? 'disabled' : ''}`}>
                                                                    <Button
                                                                        color="light"
                                                                        size="sm"
                                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                                        disabled={currentPage >= Math.ceil(totalPredictions / perPageData)}
                                                                    >
                                                                        Next
                                                                        <i className="ri-arrow-right-line"></i>
                                                                    </Button>
                                                                </li>
                                                            </ul>
                                                        </nav>
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    
                    {/* Date Selection Modal */}
                    <Modal isOpen={showDateModal} toggle={() => setShowDateModal(false)} centered>
                        <ModalHeader toggle={() => setShowDateModal(false)}>
                            Select Date View Type
                        </ModalHeader>
                        <ModalBody>
                            <p className="mb-3">
                                You selected <strong>{modalSelectedDate?.toLocaleDateString()}</strong>
                            </p>
                            <p>What would you like to view for this date?</p>
                        </ModalBody>
                        <ModalFooter>
                            <Button 
                                color="primary" 
                                onClick={() => handleDateTypeSelection('created')}
                                className="me-2"
                            >
                                <i className="ri-add-line me-1"></i>
                                Predictions Created
                            </Button>
                            <Button 
                                color="info" 
                                onClick={() => handleDateTypeSelection('expired')}
                                className="me-2"
                            >
                                <i className="ri-time-line me-1"></i>
                                Predictions Expired
                            </Button>
                            <Button 
                                color="secondary" 
                                onClick={() => setShowDateModal(false)}
                            >
                                Cancel
                            </Button>
                        </ModalFooter>
                    </Modal>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default PredictionsList;
